import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Wind, Thermometer, BatteryCharging } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { Overline } from "@/components/Section";

const TRUCKS = [
  {
    slug: "peterbilt-579",
    nickname: "ULTRALOFT",
    brand: "Peterbilt",
    model: "579 Ultraloft",
    years: "2020 – 2026",
    colorName: "Midnight Black",
    colorHex: "#111111",
    colorBorder: "border-zinc-800",
    image: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/a9ae8239a264c7acc33565389f562ed27529780f4eda6a7b5927b909898968ec.jpeg",
    accentClass: "bg-zinc-900 text-zinc-200",
    highlights: [
      "Class 8 aerodynamic high-roof sleeper",
      "PACCAR MX-13 / MX-11 engine options",
      "60\u2033 or 70\u2033 Ultra Loft sleeper bed",
      "SmartWheel steering & Kenworth cab comfort",
      "Detroit DT12 automatic transmission",
      "110V outlets + USB throughout cab",
    ],
  },
  {
    slug: "freightliner-cascadia",
    nickname: "FLAGSHIP",
    brand: "Freightliner",
    model: "Cascadia Evolution",
    years: "2020 – 2026",
    colorName: "Pearl White",
    colorHex: "#F0F0F0",
    colorBorder: "border-gray-200",
    image: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/de3a83a1a9bac504a0f32b3d3244277b29f85cad63def7661db39e92414a7f42.jpeg",
    accentClass: "bg-slate-100 text-slate-800",
    highlights: [
      "Detroit DD15 / DD13 engine options",
      "Detroit DT12 automated transmission",
      "Aerodynamic evolution cab design",
      "High-roof flat-floor sleeper",
      "Detroit Connect telematics",
      "Optional Co-Pilot driver-assistance system",
    ],
  },
  {
    slug: "kenworth-t680",
    nickname: "AERO",
    brand: "Kenworth",
    model: "T680 Next Gen",
    years: "2020 – 2026",
    colorName: "Royal Blue",
    colorHex: "#1D3F8A",
    colorBorder: "border-blue-800",
    image: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/effd7507e177e5b8280bb91e1b05492ca52110d3d7ee437d80598a16a7036671.jpeg",
    accentClass: "bg-blue-900 text-blue-100",
    highlights: [
      "PACCAR MX-13 500 HP engine",
      "Low-profile aerodynamic cab",
      "76\u2033 mid-roof or 52\u2033 day cab sleeper",
      "Kenworth Driver Assistance System (KDAS)",
      "Eaton Fuller Advantage AMT",
      "Premium driver comfort interior package",
    ],
  },
  {
    slug: "volvo-vnl",
    nickname: "VNL SERIES",
    brand: "Volvo",
    model: "VNL 660 / 760 / 860",
    years: "2020 – 2026",
    colorName: "Forest Green",
    colorHex: "#1A4731",
    colorBorder: "border-green-800",
    image: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/7dbf85d34ff3e96069e1825bac7c6c8ae4a0ffbb0fea4a12923421426dce4a0b.jpeg",
    accentClass: "bg-green-900 text-green-100",
    highlights: [
      "Volvo D13 / D16 engine options",
      "I-Shift automated manual transmission",
      "Curved panoramic windshield & sleek cab",
      "VNL 860 \u2014 luxury 70\u2033 flat-floor sleeper",
      "Volvo Active Driver Assist standard",
      "Class-leading fuel efficiency ratings",
    ],
  },
  {
    slug: "international-lt625",
    nickname: "LT SERIES",
    brand: "International",
    model: "LT625",
    years: "2020 – 2026",
    colorName: "Crimson Red",
    colorHex: "#8B1A1A",
    colorBorder: "border-red-900",
    image: "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/a6fcb5bb0e759ef67233fc3d03035372385ac968f8e414c5259bb16c704c3b64.jpeg",
    accentClass: "bg-red-900 text-red-100",
    highlights: [
      "Cummins X15 / International A26 engine",
      "Allison or Eaton Fuller transmission",
      "76\u2033 raised roof sleeper cabin",
      "Diamond Logic\u00ae electrical system",
      "OnCommand\u00ae Connection telematics",
      "Ergonomic command-center dash layout",
    ],
  },
];

const STANDARD_FEATURES = [
  {
    icon: <BatteryCharging className="w-6 h-6" />,
    label: "Inverter",
    desc: "Pure sine-wave 2000W inverter standard on all trucks",
  },
  {
    icon: <Thermometer className="w-6 h-6" />,
    label: "Refrigerator",
    desc: "12V compressor fridge — keep food fresh for days",
  },
  {
    icon: <Wind className="w-6 h-6" />,
    label: "APU",
    desc: "Auxiliary Power Unit for idle-free climate control",
  },
  {
    icon: <Zap className="w-6 h-6" />,
    label: "Opti Idle System",
    desc: "Smart engine management to reduce unnecessary idle",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 } }),
};

export default function Fleet() {
  return (
    <>
      <SEOHead
        title="Our Fleet | Peterbilt, Kenworth, Freightliner & More | Hylas Companion"
        description="Drive brand-new 2020–2026 Peterbilt 579, Freightliner Cascadia, Kenworth T680, Volvo VNL, and International LT625 trucks. Every unit equipped with APU, inverter, fridge, and Opti Idle."
        path="/fleet"
      />

      {/* ── Hero ── */}
      <section className="bg-[#0A0A0A] pt-40 pb-24 px-6 md:px-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,transparent,transparent 79px,#fff 79px,#fff 80px),repeating-linear-gradient(90deg,transparent,transparent 79px,#fff 79px,#fff 80px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <Overline light>Our Equipment</Overline>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-[0.12em] text-white mt-6 mb-6 leading-none">
              Premium<br />
              <span className="text-primary">Fleet</span>
            </h1>
            <p className="text-white/60 text-lg max-w-xl leading-relaxed">
              Every truck in our fleet is a late-model 2020–2026 unit, fully loaded for life on the road. No old iron. No half-measures.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Standard Equipment Strip ── */}
      <section className="bg-primary py-0 border-b border-[#0A0A0A]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#0A0A0A]/20">
            {STANDARD_FEATURES.map((f, i) => (
              <motion.div
                key={f.label}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                className="flex flex-col gap-1.5 px-6 py-7"
              >
                <span className="text-[#0A0A0A]">{f.icon}</span>
                <p className="font-head text-base font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">
                  {f.label}
                </p>
                <p className="text-xs text-[#0A0A0A]/70 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── "Every Truck Includes" label ── */}
      <div className="bg-[#0A0A0A] text-white py-3 text-center">
        <p className="font-head text-[10px] uppercase tracking-[0.35em] text-white/50">
          Every truck in our fleet includes all four features above — standard, no upgrades required
        </p>
      </div>

      {/* ── Truck Cards ── */}
      <section className="bg-white py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <Overline>The Lineup</Overline>
            <h2 className="font-head text-4xl md:text-5xl font-bold uppercase tracking-[0.12em] mt-4">
              5 Models. All New.
            </h2>
          </div>

          <div className="space-y-16">
            {TRUCKS.map((truck, i) => (
              <motion.article
                key={truck.slug}
                data-testid={`fleet-card-${truck.slug}`}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="border border-border group"
              >
                <div className={`grid lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  {/* Image */}
                  <div className="relative overflow-hidden aspect-[16/9] lg:aspect-auto lg:min-h-[360px] bg-[#0A0A0A]">
                    <img
                      src={truck.image}
                      alt={`${truck.brand} ${truck.model} ${truck.colorName}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    {/* Color chip */}
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#0A0A0A]/80 backdrop-blur-sm px-3 py-1.5 border border-white/10">
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-white/20 shrink-0"
                        style={{ backgroundColor: truck.colorHex }}
                      />
                      <span className="font-head text-[10px] uppercase tracking-[0.2em] text-white/80">
                        {truck.colorName}
                      </span>
                    </div>
                    {/* Nickname badge */}
                    <span className={`absolute bottom-4 right-4 font-head text-xs uppercase tracking-[0.25em] px-3 py-1.5 ${truck.accentClass}`}>
                      {truck.nickname}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-8 lg:p-10 flex flex-col justify-between">
                    <div>
                      <p className="font-head text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                        {truck.brand}  ·  {truck.years}
                      </p>
                      <h3 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-[#0A0A0A] leading-none mb-6">
                        {truck.model}
                      </h3>

                      {/* Color swatch */}
                      <div className={`inline-flex items-center gap-2 border ${truck.colorBorder} px-3 py-1.5 mb-8`}>
                        <span
                          className="w-3.5 h-3.5 rounded-full border border-black/10"
                          style={{ backgroundColor: truck.colorHex }}
                        />
                        <span className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                          Available in {truck.colorName}
                        </span>
                      </div>

                      {/* Specs */}
                      <ul className="space-y-2.5 mb-8">
                        {truck.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-2.5 text-sm text-foreground/80">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Standard equipment reminder */}
                    <div className="border-t border-border pt-5">
                      <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                        Always Included
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {STANDARD_FEATURES.map((f) => (
                          <span
                            key={f.label}
                            className="font-head text-[10px] uppercase tracking-[0.15em] bg-primary/10 text-[#0A0A0A] px-2.5 py-1 border border-primary/30"
                          >
                            {f.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-[#0A0A0A] py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Overline light>Drive This</Overline>
            <h2 className="font-head text-4xl md:text-5xl font-bold uppercase tracking-[0.12em] text-white mt-4 mb-6 leading-tight">
              One of These<br />Could Be Yours
            </h2>
            <p className="text-white/60 text-base leading-relaxed max-w-md">
              Apply today and get matched with a fully-loaded unit from our fleet. All trucks are maintained in-house and inspected before every dispatch.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/apply"
              data-testid="fleet-apply-cta"
              className="inline-flex items-center justify-center gap-3 font-head text-base uppercase tracking-[0.2em] bg-primary text-black px-8 py-5 hover:bg-white transition-colors duration-300 group"
            >
              Apply Now
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/positions"
              className="inline-flex items-center justify-center gap-3 font-head text-base uppercase tracking-[0.2em] border-2 border-white/20 text-white px-8 py-5 hover:border-primary hover:text-primary transition-colors duration-300"
            >
              View Positions
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
